import type { PayHubCashierContractsPendingWithdrawalResponse } from 'store/models';
import { percentage, subtract, sum, truncateNumber } from 'utils/math/math';

import { FULL_REVERSE_PERCENT } from '../utils';

export const PERCENT_OPTIONS = [25, 50, 75, FULL_REVERSE_PERCENT];

// fallback when the init response has no group in the withdrawal currency to take the precision from
export const DEFAULT_REVERSE_PRECISION = 2;

export interface ReverseAmounts {
  returnAmount: number;
  remainAmount: number;
}

// Mirrors BE (SDD §3.3.4): return is floored to currency precision, remain is the difference,
// so return + remain always equals the withdrawal amount. Preview only — final amounts come from the reverse response.
export const calculateReverseAmounts = (
  amount: number,
  percent: number,
  precision = DEFAULT_REVERSE_PRECISION,
): ReverseAmounts => {
  if (!Number.isFinite(amount) || !Number.isFinite(percent) || percent <= 0) {
    return {
      returnAmount: 0,
      remainAmount: amount,
    };
  }

  const returnAmount = truncateNumber(percentage(amount, percent), precision);
  // BE: remain = amount - reverse, not rounded on its own
  const remainAmount = subtract(amount, returnAmount).toNumber();

  return {
    returnAmount,
    remainAmount,
  };
};

// BE decides availability (min limit included), FE never compares with min_limit itself
export const isPercentAvailable = (withdrawal: PayHubCashierContractsPendingWithdrawalResponse, percent: number) =>
  withdrawal.available_options.includes(percent);

// Chips show only options that at least one withdrawal supports; "All" is always there
export const getAvailablePercents = (withdrawals: PayHubCashierContractsPendingWithdrawalResponse[]) =>
  PERCENT_OPTIONS.filter(
    (percent) =>
      percent === FULL_REVERSE_PERCENT || withdrawals.some((withdrawal) => isPercentAvailable(withdrawal, percent)),
  );

// Operator's option, or the next higher available one; 0 (None) or nothing higher → no preselection
export const getPreselectedPercent = (
  preselectedOption: number,
  withdrawals: PayHubCashierContractsPendingWithdrawalResponse[],
): number | null => {
  if (!preselectedOption) {
    return null;
  }

  return getAvailablePercents(withdrawals).find((percent) => percent >= preselectedOption) ?? null;
};

export const getAvailableOrderIds = (withdrawals: PayHubCashierContractsPendingWithdrawalResponse[], percent: number) =>
  withdrawals.filter((withdrawal) => isPercentAvailable(withdrawal, percent)).map(({ order_id }) => order_id);

export const calculateTotalReturn = (
  withdrawals: PayHubCashierContractsPendingWithdrawalResponse[],
  checkedIds: number[],
  percent: number,
  precision = DEFAULT_REVERSE_PRECISION,
) =>
  sum(
    withdrawals
      .filter(({ order_id }) => checkedIds.includes(order_id))
      .map(({ amount }) => calculateReverseAmounts(amount, percent, precision).returnAmount),
  ).toNumber();
